import { useState, useEffect, useRef, useId } from "react";
import "./MetricTooltip.css";
import { useGlossaryDrawer } from "../GlossaryDrawer/GlossaryDrawer";

/**
 * MetricTooltip - reusable info (i) / help (?) popover next to a metric label.
 *
 * Props:
 *   type:     "info" | "help"      (required)
 *   content:  string               (required - popover body text)
 *   position: "top"|"bottom"|"right"|"left"  (default "top"; auto-flips near edges)
 *   showAccountingNote: boolean    (info-only - appends italic accounting-method line)
 *   metricKey: string              (optional - when provided AND type="info", clicking
 *                                   the icon opens the GlossaryDrawer for that metric
 *                                   instead of the small local popover)
 *   testKey:   string              (optional - appended to data-testid for selection)
 */
export default function MetricTooltip({ type, content, position = "top", showAccountingNote = false, metricKey = "", testKey = "" }) {
  const [open, setOpen] = useState(false);
  const [resolvedPos, setResolvedPos] = useState(position);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const showTimerRef = useRef(null);
  const popId = useId();
  const drawer = useGlossaryDrawer();

  const isInfo = type === "info";
  const triggerClass = isInfo ? "mt-trigger mt-trigger-info" : "mt-trigger mt-trigger-help";
  const popClass = isInfo ? "mt-popover mt-popover-info" : "mt-popover mt-popover-help";
  const headerLabel = isInfo ? "What is this?" : "How to interpret";
  const ariaLabel = isInfo ? "What is this metric?" : "How to interpret this metric";

  const cancelShow = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };
  const scheduleShow = () => {
    cancelShow();
    /* 300ms per Section 6A of project-brief-v1. */
    showTimerRef.current = setTimeout(() => setOpen(true), 300);
  };
  const hide = () => {
    cancelShow();
    setOpen(false);
  };

  /* Auto-flip near viewport edges */
  useEffect(() => {
    if (!open || !triggerRef.current || !popoverRef.current) return;
    const tr = triggerRef.current.getBoundingClientRect();
    const pop = popoverRef.current;
    const ww = window.innerWidth;
    const wh = window.innerHeight;
    let next = position;
    if (position === "top" && tr.top < pop.offsetHeight + 16) next = "bottom";
    else if (position === "bottom" && wh - tr.bottom < pop.offsetHeight + 16) next = "top";
    if ((next === "top" || next === "bottom") && tr.right + pop.offsetWidth / 2 > ww - 8) next = "left";
    else if ((next === "top" || next === "bottom") && tr.left - pop.offsetWidth / 2 < 8) next = "right";
    setResolvedPos(next);
  }, [open, position]);

  /* Escape + click outside */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") hide(); };
    const onDoc = (e) => {
      if (!triggerRef.current || !popoverRef.current) return;
      if (triggerRef.current.contains(e.target) || popoverRef.current.contains(e.target)) return;
      hide();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  return (
    <span className="mt-wrap">
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        aria-label={ariaLabel}
        aria-describedby={open ? popId : undefined}
        aria-expanded={open}
        onMouseEnter={scheduleShow}
        onMouseLeave={(e) => {
          /* don't hide if cursor enters the popover */
          if (popoverRef.current && popoverRef.current.contains(e.relatedTarget)) return;
          hide();
        }}
        onClick={(e) => {
          e.stopPropagation();
          /* When bound to a metricKey the info trigger opens the full Glossary
             drawer. The (?) trigger keeps the local popover behavior. */
          if (isInfo && metricKey && drawer && drawer.open) {
            drawer.open(metricKey);
            hide();
            return;
          }
          setOpen((o) => !o);
        }}
        onFocus={scheduleShow}
        onBlur={(e) => {
          if (popoverRef.current && popoverRef.current.contains(e.relatedTarget)) return;
          hide();
        }}
        data-testid={`mt-${type}${testKey ? "-" + testKey : ""}`}
      >
        {isInfo ? "i" : "?"}
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popId}
          role="tooltip"
          aria-live="polite"
          className={`${popClass} mt-pos-${resolvedPos}`}
          onMouseEnter={cancelShow}
          onMouseLeave={hide}
          data-testid={`mt-${type}${testKey ? "-" + testKey : ""}-popover`}
        >
          <div className="mt-header">{headerLabel}</div>
          <div className="mt-body">{content}</div>
          {showAccountingNote && isInfo && (
            <>
              <div className="mt-divider" />
              <div className="mt-note">Reflects the accounting method (cash or accrual) and date range selected in settings.</div>
            </>
          )}
          <span className="mt-arrow" aria-hidden="true" />
        </div>
      )}
    </span>
  );
}
